<template lang="pug">
.insights-work-time-classification-widget.insights-widget
  .widget-wrapper
    .widget-common.widget-small.widget-full-height
      .widget-title
        .d-flex.align-items-baseline
          | {{ title }}
          i.hi.hi-info-filled.ml-5(
            v-if="titleTooltip"
            v-tooltip="tooltipOptions"
            :title="titleTooltip"
          )
      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          .with-data(v-if="!isEmptyWidget")
            .d-flex(v-if="!remoteVsOffice")
              .core-work
                strong {{ text }}
                span Core work

              AppChartLegend(
                :values="legendChartData()"
                orientation="vertical"
              )
            template(v-if="remoteVsOffice")
              .d-flex.flex-row.remote-vs-office
                .flex-1
                  span.d-inline-flex.align-items-center.gap-8.bold
                    span.z-bg-gray-100.p-4.d-inline-flex.rounded-16
                      .material-symbols-rounded.text-24.z-text-gray-600
                        | house
                    | Remote
                  AppChartLineStacked.flex-1.tall.mt-4.mb-8(
                    v-if="hasChartInfo('remote')"
                    :values="chartData('remote')"
                    :has-axis-legend="false"
                    has-tooltips
                  )
                  template(v-else)
                    div.chart-line-stacked.flex-1.tall.mt-10.mb-8
                      div.chart-line-stacked-canvas.small.z-bg-gray-50.d-flex.align-items-center.justify-content-center
                        span No data for remote work
                .d-flex.align-items-end.justify-content-center
                  AppChartLegend(
                    :values="legendChartData('remote')"
                    orientation="horizontal"
                    :build-label="buildLabel"
                    :show-shape="false"
                    :filter-values="false"
                    class="align-items-end"
                  )
              .d-flex.my-16.border-solid
              .d-flex.mt-10.remote-vs-office
                .flex-1
                  span.d-inline-flex.align-items-center.gap-8.bold
                    span.z-bg-gray-100.p-4.d-inline-flex.rounded-16
                      .material-symbols-rounded.text-24.z-text-gray-600
                        | corporate_fare
                    | Office
                  AppChartLineStacked.flex-1.tall.mt-10.mb-8(
                    v-if="hasChartInfo('on_site')"
                    :values="chartData('on_site')"
                    :has-axis-legend="false"
                    has-tooltips
                  )
                  template(v-else)
                    div.chart-line-stacked.flex-1.tall.mt-10.mb-8
                      div.chart-line-stacked-canvas.small.z-bg-gray-50.d-flex.align-items-center.justify-content-center
                        span No data for office work
                .d-flex.align-items-end.justify-content-center
                  AppChartLegend(
                    :values="legendChartData('on_site')"
                    orientation="horizontal"
                    :build-label="buildLabel"
                    :show-shape="false"
                    :filter-values="false"
                    class="align-items-end"
                  )
            template(v-else)
              AppChartLineStacked.mt-30(
                :values="chartData()"
                has-tooltips
              )
          InsightsNoData(
            v-else
            :type="emptyReason"
          )
          .metrics(v-if="widgetData")
            InsightsMetric(
              v-if="widgetData.to_organization_avg"
              :value="widgetData.to_organization_avg"
              text="To organization average"
              type="number"
            )
            InsightsMetric.mt-5(
              v-if="widgetData.to_role_avg"
              :value="widgetData.to_role_avg"
              text="To role average"
              type="number"
            )
            InsightsMetric.mt-5(
              v-if="widgetData.to_industry_avg"
              :value="widgetData.to_industry_avg"
              text="To industry average"
              type="number"
            )
      .widget-footer-bordered(v-if="showWidgetBenchmarks")
        InsightsBenchmarksAverages(
          :averages="benchmarksAverages"
          :pins="benchmarkPins"
          :is-loading="!isReady"
          :widget-name="title"
        )
</template>

<script>
import { defineAsyncComponent } from 'vue';

import { INSIGHTS } from '~/app_constants';
import { keysToKebabCase } from '~/utils/object_utils';
import { Insights, InsightsTracking } from '~/mixins';
import { mapGetters } from 'vuex';

import AppChartLineStacked from '~/components/AppChartLineStacked.vue';
import AppChartLegend from '~/components/AppChartLegend.vue';
import InsightsMetric from '~/components/insights/InsightsMetric.vue';

const titleTooltip = INSIGHTS.tooltips.workTimeClassification.content;

export default {
  name: 'InsightsWorkTimeClassificationWidget',
  components: {
    AppChartLineStacked,
    AppChartLegend,
    InsightsMetric,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
    InsightsBenchmarksAverages: defineAsyncComponent(() => import('~/components/insights/InsightsBenchmarksAverages.vue')),
  },
  mixins: [Insights, InsightsTracking],
  props: {
    title: {
      type: String,
      default: 'Work time classification',
    },
    chartSize: {
      type: Number,
      default: 90,
    },
    chartRadius: {
      type: Number,
      default: 38,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({ html: true }),
    titleTooltip,
    widgetKey: INSIGHTS.api.highlights.workTimeClassification,
    productivityTypes: ['unproductive', 'non-core', 'core'],
  }),
  computed: {
    ...mapGetters({
      widgetData: 'workTimeClassification',
    }),
    ...mapGetters([
      'isTeamDashboard',
      'insightsSettingsUrl',
      'canSeeTeam',
      'showBenchmarksAverages',
      'showWidgetBenchmarks',
      'remoteVsOffice',
    ]),
    coreScores() {
      return ['remote', 'on_site'].reduce((acc, locationType) => {
        acc[locationType] = this.chartData(locationType).find((item) => item.key === 'core')?.value;

        return acc;
      }, {});
    },
    hasRemoteData() {
      return this.coreScores.remote != null;
    },
    hasOfficeData() {
      return this.coreScores.on_site != null;
    },
    widgetScore() {
      if (this.isEmptyWidget) {
        return this.remoteVsOffice ? {} : 0;
      }

      if (this.remoteVsOffice) {
        const scores = {};

        if (this.hasRemoteData) { scores.remote = Math.round(this.coreScores.remote); }
        if (this.hasOfficeData) { scores.on_site = Math.round(this.coreScores.on_site); }

        return scores;
      }

      return Math.round(this.widgetData?.score || 0);
    },
    benchmarkPins() {
      const scores = this.widgetScore;

      if (typeof scores === 'object') {
        return Object.entries(scores).map(([key, value]) => ({ key, value }));
      }

      return [{ key: 'standard', value: scores }];
    },
    text() {
      return this.widgetData?.score ? `${Math.round(this.widgetData.score)}%` : '0%';
    },
    benchmarksAverages() {
      if (this.isEmptyWidget) {
        return {};
      }

      if (!this.remoteVsOffice) {
        return this.widgetData?.benchmarks || {};
      }

      const all = this.widgetData?.benchmarks || {};
      const result = {};

      if (this.hasRemoteData) { result.remote = all.remote; }
      if (this.hasOfficeData) { result.on_site = all.on_site; }

      return result;
    },
  },
  methods: {
    chartData(locationType) {
      if (!this.widgetData?.chart) {
        return [];
      }

      const buildChartData = (data) => {
        return Object.keys(data).map((key) => ({
          key,
          label: INSIGHTS.workTimeClassification[key],
          value: Number(this.fixValue(data[key])),
          tools: data[key].tools,
          tooltip: this.buildTooltip(data[key]),
        }));
      }

      if (!locationType) {
        return buildChartData(keysToKebabCase(this.widgetData.chart));
      }

      const data = keysToKebabCase(this.widgetData.chart[locationType]);

      const names = {
        remote: 'Remote',
        on_site: 'Office',
      }

      if(Object.keys(data).length === 0) {
        return this.productivityTypes.map((key) => ({
          key,
          label: INSIGHTS.workTimeClassificationRemoteVsOffice[key],
          value: null,
          tools: [],
          tooltip: this.buildTooltip(data[key] || {}, `${names[locationType]} ${key} work:`),
        }));
      }

      return this.productivityTypes.map((key) => ({
        key,
        label: INSIGHTS.workTimeClassificationRemoteVsOffice[key],
        value: Number(this.fixValue(data[key])) || 0,
        tools: data[key]?.tools || [],
        tooltip: this.buildTooltip(data[key] || {}, `${names[locationType]} ${key} work:`),
      }));
    },
    /**
     * HUB-1020 - Main issue: depending on the data and rounding methods, we get a sum more or less 100% for the chart
     * Note! We modify the original chartData object so we can use new percentage in different places.
     *
     * Solution: Find the diff between 100% and a sum of rounded percentages, add the diff to the largest value.
     * No matter if the diff is negative or positive, we work with the biggest value so we don't affect the real value so much.
     *
     * We could use another algorithm to distribute the diff proportionally to all the metrics, but
     * 1) It would be more complicated
     * 2) Using decimals in CSS can lead to unpredictable layout issues.
     */
    //
    legendChartData(key) {
      const chartData = this.chartData(key);

      if (!chartData.length) {
        return [];
      }

      if (chartData.every((item) => item.value === null)) {
        return chartData;
      }

      const valuesSum = Object.values(chartData).reduce((acc, { value }) => acc + value, 0);
      const diff = 100 - valuesSum;
      let largestValueObject = chartData[0];

      for (let i = 0; i < chartData.length; i++) {
        if (chartData[i].value > largestValueObject.value) {
          largestValueObject = chartData[i];
        }
      }

      largestValueObject.value += diff;

      return chartData;
    },
    buildLabel(row) {
      return `
        <span class="percentage d-flex align-items-center text-strong bold text-18">
          <i class="shape"></i>${row.value === null ? '--' : `${row.value}%`}
        </span>
        <span>${row.label}</span>
      `;
    },
    fixValue(value) {
      const _value = (value?.overall || 0) * 100;

      return _value.toFixed();
    },
    buildTooltip(row, title) {
      if (row.tools?.length) {
        const list = row.tools.map((tool) => `<li>${Math.round(tool.value * 100)}% ${tool.name}</li>`).join('');

        return `
          ${title ? `<span class="d-block m-0 text-left bold">${title}</span>` : ''}
          <ul class="list-unstyled m-0 text-left">${list}</ul>
        `;
      }

      return '';
    },
    hasChartInfo(locationType) {
      return Object.keys((this.widgetData?.chart || {})[locationType] || {}).length > 0;
    },
  },
};
</script>
